import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import ProviderPageTabs from "../components/ProviderPageTabs";
import {
  formatCurrency,
  getOwnershipByNPI,
  getDualInterestByNPI,
  getOwnershipDetailByNPI,
  getGeneralPaymentsByNPIAndCompany,
  getResearchOwnershipDualInterest,
  getDirectResearchByNPI,
  getResearchByNPI,
  getResearchYearCountByNPI,
  getResearchGeneralDualInterestByNPI,
  getResearchDetailByNPI,
  checkExclusions,
  getPhysicianPodFlags,
} from "../utils/cmsApi";
import { GeneralVsOwnershipChart, OwnershipVsResearchChart } from "../components/DualInterestCharts";

import { useSubscription } from "../context/SubscriptionContext";
import { useUser } from "@clerk/react";
import WatchProviderButton from "../components/WatchProviderButton";
import { watchCompany, unwatchCompany, getWatchedCompanies, queueConfirmation } from "../utils/alertsApi";
import CompareButton from "../components/CompareButton";
import { apiFetch } from "../utils/apiClient";
import SingleProviderScreenPanel from "../components/SingleProviderScreenPanel";

const ALL_YEARS = [2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025];


// ── Active vs Historical Interests ───────────────────────────────────────────
function ActiveVsHistorical({ ownership }) {
  const activeValue = ownership
    .filter((o) => o.hasActiveOwnership)
    .reduce((s, o) => s + o.valueOfInterest, 0);

  const historicalValue = ownership
    .filter((o) => !o.hasActiveOwnership)
    .reduce((s, o) => s + o.valueOfInterest, 0);

  const activeCount = ownership.filter((o) => o.hasActiveOwnership).length;
  const historicalCount = ownership.filter((o) => !o.hasActiveOwnership).length;

  const total = activeValue + historicalValue;

  // Only render when there is a mix of active and historical
  if (activeCount === 0 || historicalCount === 0) return null;
  if (total === 0) return null;

  const activePct = (activeValue / total) * 100;
  const historicalPct = (historicalValue / total) * 100;

  // Latest reported year across all records
  const latestYear = ownership.reduce((max, o) => Math.max(max, o.latestYear || 0), 0);

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">
          Active vs. historical interests
        </h2>
        <span className="text-white/25 text-xs">
          Active = reported in {latestYear}
        </span>
      </div>

      {/* Horizontal split bar */}
      <div className="flex rounded-lg overflow-hidden h-3 mb-4">
        <div
          className="bg-green-500/70 transition-all"
          style={{ width: `${activePct}%` }}
        />
        <div
          className="bg-white/20 transition-all"
          style={{ width: `${historicalPct}%` }}
        />
      </div>

      {/* Stats row */}
      <div className="flex gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2.5 h-2.5 rounded-sm bg-green-500/70 shrink-0" />
            <span className="text-white/50 text-xs">
              Active ({activeCount} {activeCount === 1 ? "company" : "companies"})
            </span>
          </div>
          <p className="text-green-400 text-lg font-mono font-medium">
            {formatCurrency(activeValue)}
          </p>
          <p className="text-white/30 text-xs mt-0.5">{activePct.toFixed(1)}% of portfolio value</p>
        </div>

        <div className="w-px bg-white/5 self-stretch" />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2.5 h-2.5 rounded-sm bg-white/20 shrink-0" />
            <span className="text-white/50 text-xs">
              Historical ({historicalCount} {historicalCount === 1 ? "company" : "companies"})
            </span>
          </div>
          <p className="text-white/50 text-lg font-mono font-medium">
            {formatCurrency(historicalValue)}
          </p>
          <p className="text-white/30 text-xs mt-0.5">{historicalPct.toFixed(1)}% of portfolio value</p>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-white/5">
        <p className="text-white/20 text-xs">
          Values represent latest reported interest value per company · Historical interests were last reported prior to {latestYear}
        </p>
      </div>
    </div>
  );
}

// ── Physician vs Family Member Split ─────────────────────────────────────────
function HolderSplit({ ownership }) {
  const latestYear = ownership.reduce((max, o) => Math.max(max, o.latestYear || 0), 0);
  const source = ownership.filter((o) => o.latestYear === latestYear);

  let physicianValue = 0;
  let familyValue = 0;
  let jointValue = 0;

  for (const o of source) {
    if (o.interestHolder === "Physician Covered Recipient") {
      physicianValue += o.valueOfInterest;
    } else if (o.interestHolder === "Immediate Family Member") {
      familyValue += o.valueOfInterest;
    } else if (o.interestHolder === "Physician and Immediate Family Member") {
      jointValue += o.valueOfInterest;
    }
  }

  // Only render if there is any family or joint involvement
  if (familyValue === 0 && jointValue === 0) return null;

  const total = physicianValue + familyValue + jointValue;
  const physicianPct = total > 0 ? (physicianValue / total) * 100 : 0;
  const familyPct = total > 0 ? (familyValue / total) * 100 : 0;
  const jointPct = total > 0 ? (jointValue / total) * 100 : 0;

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">
          Physician vs. family member interest
        </h2>
        <span className="text-white/25 text-xs">{latestYear} data</span>
      </div>

      {/* Split bar */}
      <div className="flex rounded-lg overflow-hidden h-3 mb-4">
        {physicianValue > 0 && (
          <div className="bg-amber-400/80 transition-all" style={{ width: `${physicianPct}%` }} />
        )}
        {familyValue > 0 && (
          <div className="bg-violet-500/70 transition-all" style={{ width: `${familyPct}%` }} />
        )}
        {jointValue > 0 && (
          <div className="bg-teal-500/70 transition-all" style={{ width: `${jointPct}%` }} />
        )}
      </div>

      {/* Stats row */}
      <div className="flex gap-4 flex-wrap">
        {physicianValue > 0 && (
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-sm bg-amber-400/80 shrink-0" />
              <span className="text-white/50 text-xs">Physician</span>
            </div>
            <p className="text-amber-400 text-lg font-mono font-medium">
              {formatCurrency(physicianValue)}
            </p>
            <p className="text-white/30 text-xs mt-0.5">{physicianPct.toFixed(1)}% of total</p>
          </div>
        )}

        {physicianValue > 0 && (familyValue > 0 || jointValue > 0) && (
          <div className="w-px bg-white/5 self-stretch" />
        )}

        {familyValue > 0 && (
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-sm bg-violet-500/70 shrink-0" />
              <span className="text-white/50 text-xs">Immediate Family Member</span>
            </div>
            <p className="text-violet-400 text-lg font-mono font-medium">
              {formatCurrency(familyValue)}
            </p>
            <p className="text-white/30 text-xs mt-0.5">{familyPct.toFixed(1)}% of total</p>
          </div>
        )}

        {familyValue > 0 && jointValue > 0 && (
          <div className="w-px bg-white/5 self-stretch" />
        )}

        {jointValue > 0 && (
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-sm bg-teal-500/70 shrink-0" />
              <span className="text-white/50 text-xs">Physician &amp; Family Member</span>
            </div>
            <p className="text-teal-400 text-lg font-mono font-medium">
              {formatCurrency(jointValue)}
            </p>
            <p className="text-white/30 text-xs mt-0.5">{jointPct.toFixed(1)}% of total</p>
          </div>
        )}
      </div>

      {/* Compliance note */}
      <div className="mt-4 pt-3 border-t border-white/5">
        <p className="text-white/20 text-xs">
          ⚠ Ownership interests held by immediate family members are subject to the same Stark Law and Anti-Kickback Statute disclosure requirements as physician-held interests.
        </p>
      </div>
    </div>
  );
}

// ── Interest Type Donut ───────────────────────────────────────────────────────
function InterestTypeDonut({ ownership }) {
  const INTEREST_COLORS = {
    "Common Stock":              "#fbbf24",
    "Common & Preferred Stock":  "#e8b20a",
    "Preferred Stock":           "#8b5cf6",
    "Restricted Stock / RSUs":   "#a78bfa",
    "Stock Options":             "#3b82f6",
    "Warrants":                  "#60a5fa",
    "LLC / Membership Interest": "#14b8a6",
    "Convertible Instruments":   "#f97316",
    "Partnership Interest":      "#22c55e",
    "Acquisition / Liquidation": "#ef4444",
    "Royalties / Distributions": "#ec4899",
    "Equity Investment":         "#0ea5e9",
    "Other":                     "#6b7280",
  };

  // Normalize raw terms_of_interest into categories
function categorize(terms) {
  if (!terms) return "Other";
  const t = terms.toUpperCase();

  // 1. Mixed Common & Preferred (check before individual stock checks)
  if (t.includes("COMMON") && t.includes("PREFERRED")) return "Common & Preferred Stock";

  // 2. Restricted Stock / RSUs / PRSUs (check before generic STOCK matches)
  if (t.includes("RESTRICTED STOCK") || t.includes("RSU") || t.includes("PRSU")
    || t.includes("DEFERRED STOCK UNIT")) return "Restricted Stock / RSUs";

  // 3. Stock Options (check before generic STOCK matches)
  if (t.includes("STOCK OPTION") || t.includes("OPTION TO PURCHASE")
    || t.includes("STOCK AND OR OPTIONS") || t.includes("STOCK AND/OR OPTIONS")
    || t.includes("OPTION GRANT") || t.includes("OPTIONS GRANTED")
    || t.includes("OPTIONS EXERCISED") || t.includes("EXERCISED OPTION")
    || t.includes("OPTIONS FOR SERVICES") || t.includes("OPTION FOR COMMON")
    || t.includes("NSO GRANT") || t.includes("VESTED OPTION")
    || t.includes("UNEXERCISED OPTION") || t.includes("BLACK-SCHOLES")
    || t.includes("BLACK SCHOLES") || t.includes("ADVISOR OPTION")
    || (t.includes("OPTION") && t.includes("EXERCISE"))) return "Stock Options";

  // 4. Warrants
  if (t.includes("WARRANT")) return "Warrants";

  // 5. Common Stock
  if (t.includes("COMMON STOCK") || t.includes("COMMON SHARE")
    || t.includes("COMMON UNIT") || t.includes("ORDINARY SHARE")
    || t.includes("FOUNDER") || t === "COMMON"
    || t.includes("SHARES OF STOCK") || t.includes("COMPANY STOCK")
    || t.includes("STOCK OWNERSHIP") || t.includes("STOCK HOLDER")
    || t.includes("STOCKHOLDER") || t.includes("SHARE OWNERSHIP")
    || t.includes("STOCK GRANT") || t.includes("STOCK PURCHASE")
    || t.includes("PURCHASED CORPORATE SHARE") || t.includes("EQUITY STOCK")
    || t.includes("SHARES OF THE COMPANY") || t.includes("SHARES OF COMPANY")
    || t.includes("STOCK INTEREST")) return "Common Stock";

  // 6. Preferred Stock (all series)
  if (t.includes("PREFERRED") || t.includes("SERIES A") || t.includes("SERIES B")
    || t.includes("SERIES C") || t.includes("SERIES D") || t.includes("SERIES E")
    || t.includes("SERIES F") || t.includes("SERIES SEED")) return "Preferred Stock";

  // 7. LLC / Membership Interest
  if (t.includes("LLC") || t.includes("L.L.C") || t.includes("MEMBERSHIP INTEREST")
    || t.includes("MEMBERSHIP UNIT") || t.includes("PROFIT INTEREST")
    || t.includes("PROFITS INTEREST") || t.includes("OPERATING AGREEMENT")
    || t.includes("CLASS A UNIT") || t.includes("CLASS B UNIT")
    || t.includes("CLASS C UNIT") || t.includes("CLASS D UNIT")
    || t.includes("B UNITS") || t.includes("A UNITS")
    || t.includes("UNIT HOLDER") || t.includes("UNITHOLDER")) return "LLC / Membership Interest";

  // 8. Convertible Instruments / SAFE
  if (t.includes("CONVERTIBLE") || t.includes("SAFE INVESTMENT")
    || t.includes("SAFE 20%") || t.includes("PROMISSORY NOTE")
    || t === "SAFE") return "Convertible Instruments";

  // 9. Partnership / Investment Interest
  if (t.includes("PARTNERSHIP") || t.includes("LP ") || t.includes("LLP")
    || t.includes("CAPITAL CONTRIBUTION") || t.includes("CAPITAL INVESTMENT")
    || t.includes("PARTNER CAPITAL") || t.includes("PARTNERSHIP SHARES")) return "Partnership Interest";

  // 10. Acquisition / Liquidation
  if (t.includes("ACQUISITION") || t.includes("LIQUIDAT") || t.includes("REPURCHAS")
    || t.includes("BOUGHT BACK") || t.includes("BUYOUT") || t.includes("MERGER")
    || t.includes("COMPANY SOLD") || t.includes("ASSETS SOLD")
    || t.includes("CEASED BUSINESS") || t.includes("DISSOLVED")
    || t.includes("BANKRUPTCY") || t.includes("OUT OF BUSINESS")) return "Acquisition / Liquidation";

  // 11. Royalties / Distributions
  if (t.includes("ROYALT") || t.includes("DIVIDEND") || t.includes("DISTRIBUTION")
    || t.includes("10% OF NET PROFIT") || t.includes("PROFIT BASED ON")
    || t.includes("20% RETURN") || t.includes("20% PROFIT")) return "Royalties / Distributions";

  // 12. C-Corp / S-Corp / Private Company equity
  if (t.includes("C-CORP") || t.includes("C CORP") || t.includes("S-CORP") || t.includes("S CORP")
    || t.includes("PRIVATELY HELD") || t.includes("NON-MARKETABLE") || t.includes("NON MARKETABLE")
    || t.includes("SHAREHOLDER") || t.includes("PRIVATE OWNERSHIP")) return "C-Corp / Private Equity";
  // 13. Generic equity / investment catch-alls
  if (t === "EQUITY" || t === "INVESTMENT" || t === "STOCK" || t === "SHARES" || t === "SHARES OF EQUITY"
    || t === "OWNERSHIP" || t === "INVESTOR" || t.includes("EQUITY INVESTMENT")
    || t.includes("EQUITY INTEREST") || t.includes("EQUITY OWNERSHIP")
    || t === "STOCK OWNERSHIP" || t === "PURCHASED SHARES"
    || t.includes("CASH INVESTMENT") || t.includes("PRIVATE PLACEMENT")
    || t.includes("OWNERSHIP INTEREST")) return "Equity Investment";

  return "Other";
}

  // Use active ownership only to match the active interest value stat card
  // Fall back to all ownership if no active records exist
  // Use the most recent year any ownership was reported
  const latestYear = ownership.reduce((max, o) => Math.max(max, o.latestYear || 0), 0);
  const source = ownership.filter((o) => o.latestYear === latestYear);

  // Aggregate value by category
  const categoryTotals = {};
  for (const o of source) {
    const cat = categorize(o.termsOfInterest);
    categoryTotals[cat] = (categoryTotals[cat] || 0) + o.valueOfInterest;
  }

  const total = Object.values(categoryTotals).reduce((s, v) => s + v, 0);
  if (total === 0) return null;


  // Sort by value desc, build segments
  const segments = Object.entries(categoryTotals)
    .sort((a, b) => b[1] - a[1])
    .map(([cat, value]) => ({
      cat,
      value,
      pct: (value / total) * 100,
      color: INTEREST_COLORS[cat] || INTEREST_COLORS["Other"],
    }));

  // Build SVG donut arcs
  const SIZE = 180;
  const CX = 90;
  const CY = 90;
  const R = 72;
  const INNER_R = 44;
  const GAP = 1.5; // degrees gap between segments


  function polarToCartesian(cx, cy, r, angleDeg) {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  function arcPath(cx, cy, r, innerR, startDeg, endDeg) {
    const s = polarToCartesian(cx, cy, r, startDeg + GAP / 2);
    const e = polarToCartesian(cx, cy, r, endDeg - GAP / 2);
    const si = polarToCartesian(cx, cy, innerR, startDeg + GAP / 2);
    const ei = polarToCartesian(cx, cy, innerR, endDeg - GAP / 2);
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return [
      `M ${s.x} ${s.y}`,
      `A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`,
      `L ${ei.x} ${ei.y}`,
      `A ${innerR} ${innerR} 0 ${large} 0 ${si.x} ${si.y}`,
      "Z",
    ].join(" ");
  }

  let currentAngle = 0;
  const arcs = segments.map((seg) => {
    const sweep = (seg.pct / 100) * 360;
    const start = currentAngle;
    const end = currentAngle + sweep;
    currentAngle = end;
    return { ...seg, start, end };
  });

  // Largest segment for center label
  const largest = segments[0];

  const [hoveredCat, setHoveredCat] = useState(null);
  const hoveredSeg = arcs.find((a) => a.cat === hoveredCat) || null;
  const displaySeg = hoveredSeg || null;

  return (
    <div className="card mb-6">
      <h2 className="text-white/60 text-xs uppercase tracking-widest mb-4">
        Interest type breakdown
      </h2>
      <div className="flex items-start gap-8">
        {/* Donut SVG */}
        <div className="relative shrink-0" style={{ width: SIZE, height: SIZE }}>
          <svg width={SIZE} height={SIZE}>
            {arcs.map((arc) => (
              <path
                key={arc.cat}
                d={arcPath(CX, CY, R, INNER_R, arc.start, arc.end)}
                fill={arc.color}
                opacity={hoveredCat === null || hoveredCat === arc.cat ? 0.9 : 0.25}
                style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                onMouseEnter={() => setHoveredCat(arc.cat)}
                onMouseLeave={() => setHoveredCat(null)}
              />
            ))}
            {/* Center — default: total active interest / hover: category detail */}
            {displaySeg ? (
              <>
                <text
                  x={CX}
                  y={CY - 18}
                  textAnchor="middle"
                  fill="white"
                  fontSize="9"
                  opacity={0.45}
                >
                  {displaySeg.cat.length > 12
                    ? displaySeg.cat.slice(0, 12) + "…"
                    : displaySeg.cat}
                </text>
                <text
                  x={CX}
                  y={CY - 2}
                  textAnchor="middle"
                  fill="#fbbf24"
                  fontSize="11"
                  fontWeight="600"
                >
                  {displaySeg.value >= 1000000
                    ? `$${(displaySeg.value / 1000000).toFixed(1)}M`
                    : displaySeg.value >= 1000
                    ? `$${(displaySeg.value / 1000).toFixed(0)}K`
                    : formatCurrency(displaySeg.value)}
                </text>
                <text
                  x={CX}
                  y={CY + 14}
                  textAnchor="middle"
                  fill="white"
                  fontSize="10"
                  opacity={0.5}
                >
                  {displaySeg.pct.toFixed(1)}%
                </text>
              </>
            ) : (
              <>
                <text
                  x={CX}
                  y={CY - 14}
                  textAnchor="middle"
                  fill="white"
                  fontSize="9"
                  opacity={0.45}
                >
                  Active Interest
                </text>
                <text
                  x={CX}
                  y={CY + 4}
                  textAnchor="middle"
                  fill="#fbbf24"
                  fontSize="12"
                  fontWeight="600"
                >
                  {total >= 1000000
                    ? `$${(total / 1000000).toFixed(1)}M`
                    : total >= 1000
                    ? `$${(total / 1000).toFixed(0)}K`
                    : formatCurrency(total)}
                </text>
              </>
            )}
          </svg>
        </div>

        {/* Legend */}
        <div className="flex flex-col gap-2.5 flex-1 min-w-0 pt-1">
          {segments.map((seg) => (
            <div
              key={seg.cat}
              className="flex items-center justify-between gap-3 cursor-pointer"
              onMouseEnter={() => setHoveredCat(seg.cat)}
              onMouseLeave={() => setHoveredCat(null)}
              style={{ opacity: hoveredCat === null || hoveredCat === seg.cat ? 1 : 0.35, transition: "opacity 0.15s" }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className="w-2.5 h-2.5 rounded-sm shrink-0"
                  style={{ backgroundColor: seg.color }}
                />
                <span className="text-white/60 text-xs truncate">{seg.cat}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-white/50 text-xs font-mono">{formatCurrency(seg.value)}</span>
                <span className="text-white/25 text-xs w-10 text-right">{seg.pct.toFixed(1)}%</span>
              </div>
            </div>
          ))}
          <p className="text-white/20 text-xs mt-2 pt-2 border-t border-white/5">
            {ownership.some((o) => o.hasActiveOwnership)
              ? "Based on active (2025) ownership interests"
              : "Based on latest reported interest value per company"}
          </p>

        </div>
      </div>
    </div>
  );
}


// ── Normalized Stacked Bar Chart (100%) ──────────────────────────────────────
function NormalizedStackedBarChart({ data, companies, selectedCompanies }) {
  const years = ALL_YEARS;

  const COLORS = [
    "rgba(251,191,36,0.8)",
    "rgba(139,92,246,0.8)",
    "rgba(34,197,94,0.8)",
    "rgba(59,130,246,0.8)",
    "rgba(239,68,68,0.8)",
    "rgba(249,115,22,0.8)",
    "rgba(20,184,166,0.8)",
    "rgba(236,72,153,0.8)",
  ];

  const companyColorMap = new Map(
    companies.map((c, i) => [c, COLORS[i % COLORS.length]])
  );

  const visibleCompanies = selectedCompanies.has("ALL")
    ? companies
    : companies.filter((c) => selectedCompanies.has(c));

  const yearStacks = years.map((y) => {
    const segments = visibleCompanies.map((company) => {
      const row = data.find(
        (d) => d.programYear === y && d.companyName === company
      );
      return { company, value: row ? row.valueOfInterest : 0 };
    });
    const total = segments.reduce((s, seg) => s + seg.value, 0);
    // Normalize to 100%
    const normalized = segments.map((seg) => ({
      ...seg,
      pct: total > 0 ? (seg.value / total) * 100 : 0,
    }));
    return { year: y, segments: normalized, total };
  });

  return (
    <div className="relative" style={{ height: "180px" }}>
      <div className="absolute inset-0 flex items-end gap-1.5 pb-6 pt-8">
        {yearStacks.map(({ year, segments, total }) => {
          const activeSegments = segments.filter((s) => s.value > 0);
          const hasData = total > 0;
          return (
            <div
              key={year}
              className="flex-1 flex flex-col items-center justify-end h-full group relative"
            >
              {/* Hover tooltip */}
              {hasData && (
                <div
                  className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none"
                  style={{ minWidth: "140px" }}
                >
                  <div className="flex items-center justify-between gap-4 mb-1.5 pb-1.5 border-b border-white/10">
                    <p className="text-white/50 font-medium">{year}</p>
                    <p className="text-amber-400 font-medium">100%</p>
                  </div>
                  {activeSegments.map(({ company, value, pct }) => (
                    <div key={company} className="flex items-center justify-between gap-3 mt-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <div
                          className="w-2 h-2 rounded-sm shrink-0"
                          style={{ backgroundColor: companyColorMap.get(company) }}
                        />
                        <span className="text-white/40 truncate" style={{ maxWidth: "90px" }}>
                          {company.length > 18 ? company.slice(0, 18) + "…" : company}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-white/60">{pct.toFixed(1)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Normalized stacked bar — always full height when data exists */}
              <div
                className="w-full rounded-t overflow-hidden flex flex-col-reverse"
                style={{ height: hasData ? "calc(100% - 24px)" : "3px" }}
              >
                {segments.map(({ company, value, pct }) =>
                  value > 0 ? (
                    <div
                      key={company}
                      style={{
                        height: `${pct}%`,
                        backgroundColor: companyColorMap.get(company),
                        minHeight: "2px",
                      }}
                    />
                  ) : null
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Y-axis labels */}
      <div className="absolute left-0 top-0 bottom-6 flex flex-col justify-between items-end pr-1 pointer-events-none" style={{ width: "28px" }}>
        {["100%", "75%", "50%", "25%", "0%"].map((label) => (
          <span key={label} className="text-white/20" style={{ fontSize: "8px" }}>
            {label}
          </span>
        ))}
      </div>

      {/* Year labels */}
      <div className="absolute bottom-0 flex gap-1.5" style={{ left: "30px", right: "0" }}>
        {years.map((year) => (
          <div key={year} className="flex-1 text-center">
            <span className="text-white/30" style={{ fontSize: "10px" }}>
              {year}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Ownership Stacked Bar Chart ───────────────────────────────────────────────
function OwnershipStackedBarChart({ data, companies, selectedCompanies, isPro }) {
  const years = ALL_YEARS;

  const COLORS = [
    "rgba(251,191,36,0.8)",
    "rgba(139,92,246,0.8)",
    "rgba(34,197,94,0.8)",
    "rgba(59,130,246,0.8)",
    "rgba(239,68,68,0.8)",
    "rgba(249,115,22,0.8)",
    "rgba(20,184,166,0.8)",
    "rgba(236,72,153,0.8)",
  ];

  const companyColorMap = new Map(
    companies.map((c, i) => [c, COLORS[i % COLORS.length]])
  );

  const visibleCompanies = selectedCompanies.has("ALL")
    ? companies
    : companies.filter((c) => selectedCompanies.has(c));

  const yearStacks = years.map((y) => {
    const segments = visibleCompanies.map((company) => {
      const row = data.find(
        (d) => d.programYear === y && d.companyName === company
      );
      return { company, value: row ? row.valueOfInterest : 0 };
    });
    const total = segments.reduce((s, seg) => s + seg.value, 0);
    return { year: y, segments, total };
  });

  const maxTotal = Math.max(...yearStacks.map((y) => y.total), 1);

  return (
    <div className="relative" style={{ height: "180px" }}>
      <div className="absolute inset-0 flex items-end gap-1.5 pb-6 pt-8" style={{ left: "30px" }}>
        {yearStacks.map(({ year, segments, total }) => {
          const heightPct = (total / maxTotal) * 100;
          const activeSegments = segments.filter((s) => s.value > 0);
          return (
            <div
              key={year}
              className="flex-1 flex flex-col items-center justify-end h-full group relative"
            >
              {/* ── Hover tooltip ───────────────────────────────────────── */}
              {total > 0 && (
                <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none"
                  style={{ minWidth: "140px" }}>
                  {/* Year + total */}
                  <div className="flex items-center justify-between gap-4 mb-1.5 pb-1.5 border-b border-white/10">
                    <p className="text-white/50 font-medium">{year}</p>
                    <p className="text-amber-400 font-medium">{formatCurrency(total)}</p>
                  </div>
                  {/* Per-company breakdown */}
                  {activeSegments.map(({ company, value }) => (
                    <div key={company} className="flex items-center justify-between gap-3 mt-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <div
                          className="w-2 h-2 rounded-sm shrink-0"
                          style={{ backgroundColor: companyColorMap.get(company) }}
                        />
                        <span className="text-white/40 truncate" style={{ maxWidth: "90px" }}>
                          {company.length > 18 ? company.slice(0, 18) + "…" : company}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-white/60">{formatCurrency(value)}</span>
                        <span className="text-white/30">
                          {total > 0 ? `${((value / total) * 100).toFixed(1)}%` : "0.0%"}
                        </span>
                      </div>
                    </div>
                  ))}                  
                </div>
              )}

              {/* ── Total label above bar ────────────────────────────────── */}
              {total > 0 && (
                <div
                  className="absolute w-full text-center pointer-events-none"
                  style={{ bottom: `${Math.max(heightPct, 3)}%`, paddingBottom: "3px" }}
                >
                  <span className="text-amber-400/80 font-medium" style={{ fontSize: "10px" }}>
                    {total >= 1000000
                      ? `$${(total / 1000000).toFixed(1)}M`
                      : total >= 1000
                      ? `$${(total / 1000).toFixed(0)}K`
                      : `$${total.toFixed(0)}`}
                  </span>
                </div>
              )}

              {/* ── Stacked bar ─────────────────────────────────────────── */}
              <div
                className="w-full rounded-t overflow-hidden flex flex-col-reverse"
                style={{ height: total > 0 ? `${Math.max(heightPct, 3)}%` : "3px" }}
              >
                {segments.map(({ company, value }) =>
                  value > 0 ? (
                    <div
                      key={company}
                      style={{
                        height: `${(value / total) * 100}%`,
                        backgroundColor: companyColorMap.get(company),
                        minHeight: "2px",
                      }}
                    />
                  ) : null
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Year labels ─────────────────────────────────────────────────── */}
      <div className="absolute bottom-0 left-0 right-0 flex gap-1.5">
        {years.map((year) => (
          <div key={year} className="flex-1 text-center">
            <span className="text-white/30" style={{ fontSize: "10px" }}>
              {year}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Company filter dropdown (shared by both charts) ───────────────────────────
function CompanyDropdown({ companies, selectedCompanies, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const isAll = selectedCompanies.has("ALL");

  function label() {
    if (isAll) return "All companies";
    if (selectedCompanies.size === 1) {
      const name = [...selectedCompanies][0];
      return name.length > 30 ? name.slice(0, 30) + "..." : name;
    }
    return `${selectedCompanies.size} companies selected`;
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 bg-navy-700 border border-white/10 text-white/70 rounded-lg px-3 py-1.5 text-xs hover:border-white/20 transition-colors"
      >
        <span>{label()}</span>
        <span className="text-white/30">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-navy-800 border border-white/10 rounded-xl overflow-hidden z-50 shadow-xl min-w-56 max-h-64 overflow-y-auto">
          <button
            onClick={() => onToggle("ALL")}
            className={`w-full text-left px-4 py-2.5 text-xs flex items-center gap-2 transition-colors border-b border-white/5 ${
              isAll ? "text-amber-400 bg-amber-500/10" : "text-white/60 hover:text-white hover:bg-navy-700"
            }`}
          >
            <span className={`w-3 h-3 rounded border flex items-center justify-center shrink-0 ${isAll ? "bg-amber-500 border-amber-500" : "border-white/20"}`}>
              {isAll && <span className="text-navy-900 text-xs leading-none">✓</span>}
            </span>
            All companies
          </button>
          {companies.map((name) => {
            const selected = selectedCompanies.has(name);
            return (
              <button
                key={name}
                onClick={() => onToggle(name)}
                className={`w-full text-left px-4 py-2.5 text-xs flex items-center gap-2 transition-colors border-b border-white/5 last:border-0 ${
                  selected ? "text-amber-400 bg-amber-500/5" : "text-white/60 hover:text-white hover:bg-navy-700"
                }`}
              >
                <span className={`w-3 h-3 rounded border flex items-center justify-center shrink-0 ${selected ? "bg-amber-500 border-amber-500" : "border-white/20"}`}>
                  {selected && <span className="text-navy-900 text-xs leading-none">✓</span>}
                </span>
                <span className="truncate">{name.length > 35 ? name.slice(0, 35) + "..." : name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Collapsible conflict banner ───────────────────────────────────────────────
function CollapsibleBanner({ color, icon, title, description, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const colorMap = {
    red:   { border: "border-red-500/30",   bg: "bg-red-500/10",   icon: "text-red-400",   title: "text-red-400",   chevron: "text-red-400/60"   },
    amber: { border: "border-amber-500/30", bg: "bg-amber-500/10", icon: "text-amber-400", title: "text-amber-400", chevron: "text-amber-400/60" },
    teal:  { border: "border-teal-500/30",  bg: "bg-teal-500/10",  icon: "text-teal-400",  title: "text-teal-400",  chevron: "text-teal-400/60"  },
    blue:  { border: "border-blue-500/30",  bg: "bg-blue-500/10",  icon: "text-blue-400",  title: "text-blue-400",  chevron: "text-blue-400/60"  },
  };
  const c = colorMap[color] || colorMap.amber;
  return (
    <div className={`${c.bg} border ${c.border} rounded-xl overflow-hidden`}>
      <button onClick={() => setOpen(!open)} className="print:hidden w-full flex items-start gap-2 p-4 text-left">
        <span className={`${c.icon} text-sm mt-0.5 shrink-0`}>{icon}</span>
        <div className="flex-1 min-w-0">
          <p className={`${c.title} font-medium text-sm`}>{title}</p>
          <p className="text-white/50 text-xs mt-1 leading-relaxed">{description}</p>
        </div>
        <span className={`${c.chevron} text-xs mt-0.5 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}>▼</span>
      </button>
      <div className="hidden print:flex items-start gap-2 p-4">
        <span className={`${c.icon} text-sm mt-0.5 shrink-0`}>{icon}</span>
        <div className="flex-1 min-w-0">
          <p className={`${c.title} font-medium text-sm`}>{title}</p>
          <p className="text-white/50 text-xs mt-1 leading-relaxed">{description}</p>
        </div>
      </div>
      {open && children && (
        <div className="px-4 pb-4 pt-0 border-t border-white/5">{children}</div>
      )}
      {/* Print: no children — header summary only */}
    </div>
  );
}

function OwnersConflictBanners({
  hasTripleInterest, tripleInterestCompanyIds,
  hasDualInterest, hasDualInterestSamePeriod, dualInterest,
  dualResearchInterest, researchGeneralDualInterest, hasResearchGeneralDual,
  directResearch, directResearchCompanyIds,
  podFlags,
  isPro, setUpgradeMessage, setShowUpgradeModal,
}) {
  const hasPodRisk = podFlags && podFlags.length > 0;
  const tier1Pod = podFlags.filter((p) => p.podTier === 1);
  const tier2Pod = podFlags.filter((p) => p.podTier === 2);

  return (
    <>
      {hasPodRisk && (
        <CollapsibleBanner
          color="blue"
          icon="⚠"
          title={`Potential POD Pattern Detected (${tier1Pod.length + tier2Pod.length} ${tier1Pod.length + tier2Pod.length === 1 ? "company" : "companies"})`}
          description={`This provider holds or has held an ownership interest in ${tier1Pod.length + tier2Pod.length === 1 ? "a company" : `${tier1Pod.length + tier2Pod.length} companies`} identified as a potential Physician Owned Distributorship (POD) — an arrangement flagged as high-risk in the OIG Work Plan 2026. PODs involve physicians holding equity in companies that distribute medical devices, while also receiving payments from those companies.`}
          defaultOpen={false}
        >
          {isPro ? (
            <div className="space-y-2 mt-3">
              {podFlags.map((p, i) => (
                <div key={i} className="bg-blue-500/5 border border-blue-500/20 rounded-lg px-3 py-2">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-blue-300 text-xs font-medium">{p.companyName}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full border ${
                          p.podTier === 1
                            ? "bg-blue-500/20 border-blue-500/30 text-blue-300"
                            : "bg-blue-500/10 border-blue-500/20 text-blue-400"
                        }`}>
                          Tier {p.podTier} — {p.podTier === 1 ? "High Confidence" : "Elevated Risk"}
                        </span>
                        {p.hasSameYearOverlap && (
                          <span className="bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs px-1.5 py-0.5 rounded-full">
                            Same-year overlap
                          </span>
                        )}
                        {p.hasDistributorName && (
                          <span className="bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs px-1.5 py-0.5 rounded-full">
                            Distributor name signal
                          </span>
                        )}
                        {p.hasDistributionTerm && (
                          <span className="bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs px-1.5 py-0.5 rounded-full">
                            Distribution term signal
                          </span>
                        )}
                      </div>
                      <p className="text-blue-300/50 text-xs mt-0.5">
                        Ownership reported {p.firstOwnershipYear}
                        {p.lastOwnershipYear !== p.firstOwnershipYear ? `–${p.lastOwnershipYear}` : ""}
                        {" · "}
                        {p.physicianOwnerCount} physician owner{p.physicianOwnerCount !== 1 ? "s" : ""}
                        {" · "}
                        {p.pctOwnersWithPayments.toFixed(0)}% also receive payments
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-blue-400 text-xs font-mono font-medium">
                        {formatCurrency(p.totalOwnershipValue)}
                      </p>
                      <p className="text-blue-300/40 text-xs">ownership value</p>
                      {p.generalPaymentsTotal > 0 && (
                        <>
                          <p className="text-white/40 text-xs font-mono mt-1">
                            {formatCurrency(p.generalPaymentsTotal)}
                          </p>
                          <p className="text-white/30 text-xs">general payments</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <p className="text-blue-300/30 text-xs pt-1">
                POD flags are risk-review indicators, not legal determinations. Source: CMS Open Payments Ownership data · OIG Work Plan 2026. Organizations should consult qualified legal and compliance professionals for final determinations.
              </p>
            </div>
          ) : (
            <button
              onClick={() => {
                setUpgradeMessage("Upgrade to Pro to see full POD risk details including company names, ownership values, and payment overlap analysis.");
                setShowUpgradeModal(true);
              }}
              className="mt-3 text-xs px-3 py-1.5 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 transition-all"
            >
              🔒 Upgrade to Pro to see details
            </button>
          )}
        </CollapsibleBanner>
      )}
      {hasTripleInterest && (
        <CollapsibleBanner
          color="red" icon="⚠"
          title="Triple Interest Conflict"
          description={`This provider has general payments, ownership interests, and research funding from the same ${tripleInterestCompanyIds.size === 1 ? "company" : `${tripleInterestCompanyIds.size} companies`}. This combination carries simultaneous Stark Law, Anti-Kickback Statute, and research integrity exposure.`}
          defaultOpen={false}
        >
          {isPro ? (
            <div className="space-y-2 mt-3">
              {dualInterest.filter((d) => tripleInterestCompanyIds.has(d.companyId)).map((d, i) => {
                const resGen = researchGeneralDualInterest.find((g) => g.companyId === d.companyId);
                const resDual = dualResearchInterest.find((r) => r.companyId === d.companyId);
                return (
                  <div key={i} className="bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-red-300 text-xs font-medium">{d.companyName}</p>
                      {d.hasTripleInterestSameYear && <span className="bg-red-500/30 border border-red-500/50 text-red-200 text-xs px-1.5 py-0.5 rounded-full font-medium">⚑ All three same year</span>}
                    </div>
                    <div className="flex gap-2 flex-wrap mt-1">
                      {d.hasDualInterestSamePeriod && <span className="bg-red-500/20 border border-red-500/30 text-red-300 text-xs px-1.5 py-0.5 rounded-full">Payments+Ownership overlap</span>}
                      {resDual?.hasDualInterestSamePeriod && <span className="bg-red-500/20 border border-red-500/30 text-red-300 text-xs px-1.5 py-0.5 rounded-full">Research+Ownership overlap</span>}
                      {resGen?.hasDualInterestSamePeriod && <span className="bg-red-500/20 border border-red-500/30 text-red-300 text-xs px-1.5 py-0.5 rounded-full">Research+Payments overlap</span>}
                      {resGen?.highPaymentsPreclinicalResearch && <span className="bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs px-1.5 py-0.5 rounded-full">Preclinical</span>}
                    </div>
                    <div className="flex gap-4 mt-2 flex-wrap">
                      <div><p className="text-white/40 text-xs font-mono">{formatCurrency(d.generalPaymentsTotal)}</p><p className="text-white/25 text-xs">general payments</p></div>
                      <div><p className="text-amber-400 text-xs font-mono">{formatCurrency(d.valueOfInterest)}</p><p className="text-white/25 text-xs">ownership value</p></div>
                      {resGen && <div><p className="text-teal-400 text-xs font-mono">{formatCurrency(resGen.researchTotal)}</p><p className="text-white/25 text-xs">research funding</p></div>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <button onClick={() => { setUpgradeMessage("Upgrade to Pro to see full triple interest conflict details."); setShowUpgradeModal(true); }} className="mt-3 text-xs px-3 py-1.5 rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 hover:bg-red-500/30 transition-all">🔒 Upgrade to Pro to see details</button>
          )}
        </CollapsibleBanner>
      )}

      {hasDualInterest && dualInterest.some((d) => !tripleInterestCompanyIds.has(d.companyId)) && (
        <CollapsibleBanner
          color="amber" icon="⚑"
          title="General & Ownership Conflict"
          description={`This provider holds or has held an ownership or investment interest in ${dualInterest.filter((d) => !tripleInterestCompanyIds.has(d.companyId)).length === 1 ? "a company" : `${dualInterest.filter((d) => !tripleInterestCompanyIds.has(d.companyId)).length} companies`} that also reported general payments to them.${hasDualInterestSamePeriod ? " Ownership and payments overlap in the same reporting period." : ""}`}
        >
          {isPro ? (
            <div className="space-y-2 mt-3">
              {dualInterest.filter((d) => !tripleInterestCompanyIds.has(d.companyId)).map((d, i) => (
                <div key={i} className="bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-amber-300 text-xs font-medium">{d.companyName}</p>
                        {d.hasDualInterestSamePeriod && (
                          <span className="bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs px-1.5 py-0.5 rounded-full">Same period</span>
                        )}
                      </div>
                      <p className="text-amber-300/50 text-xs mt-0.5">
                        {d.hasDualInterestSamePeriod ? `Ownership and payments overlap · ${d.ownershipFirstYear}–${d.ownershipLatestYear}` : `Ownership: ${d.ownershipFirstYear}–${d.ownershipLatestYear} · Payments through ${d.generalPaymentsLatestYear}`}
                      </p>
                      <p className="text-amber-300/40 text-xs mt-0.5">
                        {d.interestHolder === "Physician and Immediate Family Member" ? "Held by physician and immediate family member" : d.interestHolder === "Immediate Family Member" ? "Held by immediate family member" : "Held by physician"}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-amber-400 text-xs font-mono font-medium">{formatCurrency(d.valueOfInterest)}</p>
                      <p className="text-amber-300/40 text-xs">ownership value</p>
                      <p className="text-white/40 text-xs font-mono mt-1">{formatCurrency(d.generalPaymentsTotal)}</p>
                      <p className="text-white/30 text-xs">general payments</p>
                    </div>
                  </div>
                  {d.termsOfInterest && <p className="text-amber-300/30 text-xs mt-2 italic line-clamp-2">{d.termsOfInterest}</p>}
                </div>
              ))}
            </div>
          ) : (
            <button onClick={() => { setUpgradeMessage("Upgrade to Pro to see full ownership and dual interest details."); setShowUpgradeModal(true); }} className="mt-3 text-xs px-3 py-1.5 rounded-lg bg-amber-500/20 border border-amber-500/30 text-amber-300 hover:bg-amber-500/30 transition-all">🔒 Upgrade to Pro to see details</button>
          )}
        </CollapsibleBanner>
      )}

      {(() => {
        // Combine PI-based and direct research ownership conflicts, excluding triple interest
        const piRows = dualResearchInterest.filter((r) => !tripleInterestCompanyIds.has(r.companyId));
        // Direct-only: companies with direct research + ownership but no PI-based research conflict row
        const directOnlyRows = (directResearch || []).filter((d) =>
          !tripleInterestCompanyIds.has(d.companyId) &&
          !dualResearchInterest.some((r) => r.companyId === d.companyId) &&
          // Only include if also in ownership (directResearchCompanyIds checked at parent)
          piRows.every((r) => r.companyId !== d.companyId)
        );
        const allResearchOwnershipRows = [...piRows];
        const hasAny = allResearchOwnershipRows.length > 0;
        if (!hasAny) return null;
        return (
          <CollapsibleBanner
            color="teal" icon="⚗"
            title="Ownership & Research Conflict"
            description={`This provider holds or has held an ownership interest in ${allResearchOwnershipRows.length === 1 ? "a company" : `${allResearchOwnershipRows.length} companies`} that also funded research payments to them.${allResearchOwnershipRows.some((r) => r.hasDualInterestSamePeriod) ? " Research funding and ownership overlap in the same reporting period." : ""}`}
          >
            {isPro ? (
              <div className="space-y-2 mt-3">
                {allResearchOwnershipRows.map((r, i) => (
                  <div key={i} className="bg-teal-500/5 border border-teal-500/20 rounded-lg px-3 py-2">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-teal-300 text-xs font-medium">{r.companyName}</p>
                          {r.paymentType === "direct" && (
                            <span className="bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs px-1.5 py-0.5 rounded-full">Direct payment</span>
                          )}
                          {r.hasDualInterestSamePeriod && (
                            <span className="bg-teal-500/20 border border-teal-500/30 text-teal-400 text-xs px-1.5 py-0.5 rounded-full">Same period</span>
                          )}
                          {r.hasPreclinical && (
                            <span className="bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs px-1.5 py-0.5 rounded-full">Preclinical</span>
                          )}
                        </div>
                        <p className="text-teal-300/50 text-xs mt-0.5">
                          {r.paymentType === "direct"
                            ? `Direct research payments · ${r.researchFirstYear}–${r.researchLatestYear} · Ownership: ${r.ownershipFirstYear}–${r.ownershipLatestYear}`
                            : r.hasDualInterestSamePeriod
                            ? `Research and ownership overlap · ${Math.max(r.researchFirstYear, r.ownershipFirstYear)}–${Math.min(r.researchLatestYear, r.ownershipLatestYear)}`
                            : `Research: ${r.researchFirstYear}–${r.researchLatestYear} · Ownership: ${r.ownershipFirstYear}–${r.ownershipLatestYear}`}
                        </p>
                        <p className="text-teal-300/40 text-xs mt-0.5">
                          {r.interestHolder === "Physician and Immediate Family Member"
                            ? "Held by physician and immediate family member"
                            : r.interestHolder === "Immediate Family Member"
                            ? "Held by immediate family member"
                            : "Held by physician"}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-teal-400 text-xs font-mono font-medium">{formatCurrency(r.researchTotal)}</p>
                        <p className="text-teal-300/40 text-xs">{r.paymentType === "direct" ? "direct payments" : "research funding"}</p>
                        <p className="text-white/40 text-xs font-mono mt-1">{formatCurrency(r.ownershipValue)}</p>
                        <p className="text-white/30 text-xs">ownership value</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <button
                onClick={() => { setUpgradeMessage("Upgrade to Pro to see full ownership and research conflict details."); setShowUpgradeModal(true); }}
                className="mt-3 text-xs px-3 py-1.5 rounded-lg bg-teal-500/20 border border-teal-500/30 text-teal-300 hover:bg-teal-500/30 transition-all"
              >
                🔒 Upgrade to Pro to see details
              </button>
            )}
          </CollapsibleBanner>
        );
      })()}

    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PhysicianOwnersPage() {
  const { npi } = useParams();
  const [searchParams] = useSearchParams();
  const name = searchParams.get("name") || "This provider";
  const providerState = searchParams.get("state") || "";
  const specialty = searchParams.get("specialty") || "";
  const totalPayments = parseFloat(searchParams.get("totalPayments") || "0");

  const { isPro, isEnterprise, setShowUpgradeModal, setUpgradeMessage } = useSubscription();
  const EXPORT_LIMIT = isEnterprise ? Infinity : 500;
  const { user, isSignedIn } = useUser();

  // ── Data state ───────────────────────────────────────────────────────────
  const [ownership, setOwnership] = useState([]);
  const [dualInterest, setDualInterest] = useState([]);
  const [detail, setDetail] = useState([]);
  const [generalPayments, setGeneralPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isWatched, setIsWatched] = useState(false);
  const [watchedCompanyNames, setWatchedCompanyNames] = useState(new Map());
  const [companyWatchLoading, setCompanyWatchLoading] = useState(new Set());
  const [screenPanelCompanyId, setScreenPanelCompanyId] = useState(null);
  const [exclusions, setExclusions] = useState({ oig: [], sam: [] });
  const [exclusionLoading, setExclusionLoading] = useState(true);


  // ── Chart state ──────────────────────────────────────────────────────────
  const [selectedCompanies, setSelectedCompanies] = useState(new Set(["ALL"]));
  const [stackedView, setStackedView] = useState("absolute");
  const [dualResearchInterest, setDualResearchInterest] = useState([]);
  const [researchGeneralDualInterest, setResearchGeneralDualInterest] = useState([]);
  const [research, setResearch] = useState([]);
  const [researchYearCount, setResearchYearCount] = useState(0);
  const [researchDetail, setResearchDetail] = useState([]);
  const [directResearch, setDirectResearch] = useState([]);
  const [podFlags, setPodFlags] = useState([]);

  // ── Fetch all ownership data in parallel ─────────────────────────────────
  useEffect(() => {
    if (!npi || npi === "unknown") {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      getOwnershipByNPI(npi),
      getDualInterestByNPI(npi),
      getOwnershipDetailByNPI(npi),
      getPhysicianPodFlags(npi),
    ])
      .then(([ownershipData, dualData, detailData, podData]) => {
        setOwnership(ownershipData || []);
        setDualInterest(dualData || []);
        setDetail(detailData || []);
        setPodFlags(podData || []);
        // Fetch year-by-year general payments for dual interest companies
        const dualCompanyIds = (dualData || [])
          .filter((d) => d.hasDualInterestAnyYear)
          .map((d) => d.companyId)
          .filter(Boolean);
        if (dualCompanyIds.length > 0) {
          getGeneralPaymentsByNPIAndCompany(npi, dualCompanyIds)
            .then((gp) => setGeneralPayments(gp || []))
            .catch(() => setGeneralPayments([]));
        }
        Promise.all([
          getResearchOwnershipDualInterest(npi),
          getResearchByNPI(npi),
          getResearchYearCountByNPI(npi),
          getResearchGeneralDualInterestByNPI(npi),
        ])
          .then(([dualRes, researchData, yearCountData, resGenData]) => {
            setDualResearchInterest(dualRes || []);
            setResearch(researchData || []);
            setResearchYearCount(yearCountData?.yearCount || 0);
            setResearchGeneralDualInterest(resGenData || []);
            // Fetch research detail for ownership vs research chart
            if ((dualRes || []).length > 0) {
              getResearchDetailByNPI(npi)
                .then((rd) => setResearchDetail(rd || []))
                .catch(() => setResearchDetail([]));
            }
            // Fetch direct research payments
            getDirectResearchByNPI(npi)
              .then((dr) => setDirectResearch(dr || []))
              .catch(() => setDirectResearch([]));
          })
          .catch(() => {
            setDualResearchInterest([]);
            setResearch([]);
            setResearchYearCount(0);
            setResearchGeneralDualInterest([]);
            setDirectResearch([]);
          });
      })
      .catch(() => {
        setOwnership([]);
        setDualInterest([]);
        setDetail([]);
        setGeneralPayments([]);
      })
      .finally(() => setLoading(false));
  }, [npi]);

useEffect(() => {
    if (!npi || npi === "unknown") { setExclusionLoading(false); return; }
    setExclusionLoading(true);
    const nameParts = name.split(" ");
    const first = nameParts[0] || "";
    const last = nameParts[nameParts.length - 1] || "";
    checkExclusions(npi, first, last, providerState)
      .then((data) => setExclusions({ oig: data.oig || [], sam: data.sam || [] }))
      .catch(() => setExclusions({ oig: [], sam: [] }))
      .finally(() => setExclusionLoading(false));
  }, [npi, name]);

  useEffect(() => {
    if (!isSignedIn || !user) return;
    getWatchedCompanies(user.id)
      .then((data) => {
        setWatchedCompanyNames(new Map(data.map((c) => [c.company_name, true])));
      })
      .catch(() => {});
  }, [isSignedIn, user]);

  async function handleCompanyWatchToggle(e, companyName, companyId) {
    e.preventDefault();
    e.stopPropagation();
    if (!isSignedIn) {
      setUpgradeMessage("Sign in to watch companies and receive email alerts.");
      setShowUpgradeModal(true);
      return;
    }
    if (!isPro) {
      setUpgradeMessage("Watching companies is a Pro feature. Upgrade to get notified when new payment data is published.");
      setShowUpgradeModal(true);
      return;
    }
    setCompanyWatchLoading((prev) => new Set(prev).add(companyName));
    try {
      if (watchedCompanyNames.get(companyName)) {
        await unwatchCompany(user.id, companyName);
        setWatchedCompanyNames((prev) => new Map(prev).set(companyName, false));
      } else {
        await watchCompany(user.id, companyName, companyId);
        await queueConfirmation(user.id, "company", companyName, companyName, { alert_payments: true });
        setWatchedCompanyNames((prev) => new Map(prev).set(companyName, true));
      }
    } catch (err) {
      console.error("Company watch toggle error:", err);
    } finally {
      setCompanyWatchLoading((prev) => { const s = new Set(prev); s.delete(companyName); return s; });
    }
  }

  function toggleCompany(name) {
    if (name === "ALL") {
      setSelectedCompanies(new Set(["ALL"]));
      return;
    }
    const next = new Set(selectedCompanies);
    next.delete("ALL");
    if (next.has(name)) {
      next.delete(name);
      if (next.size === 0) next.add("ALL");
    } else {
      next.add(name);
    }
    setSelectedCompanies(next);
  }

  // ── Derived values ────────────────────────────────────────────────────────
  const activeOwnership = ownership.filter((o) => o.hasActiveOwnership);
  const hasDualInterest = dualInterest.length > 0;
  const hasDualInterestSamePeriod = dualInterest.some((d) => d.hasDualInterestSamePeriod);
  const hasResearchGeneralDual = researchGeneralDualInterest.length > 0;
  // Direct research company IDs — companies paying physician directly for research
  const directResearchCompanyIds = new Set(directResearch.map((d) => d.companyId));

  const tripleInterestCompanyIds = new Set(
    dualInterest
      .filter((d) => d.hasTripleInterestAnyYear)
      .map((d) => d.companyId)
  );
  const hasTripleInterest = tripleInterestCompanyIds.size > 0;
  const totalCurrentValue = activeOwnership.reduce((s, o) => s + o.valueOfInterest, 0);
  const hasResearch = research.length > 0;
  const activeResearch = research.filter((r) => r.hasActiveResearch);
  const totalResearchPayments = research.reduce((s, r) => s + r.paymentCount, 0); 

  // Companies with ownership data for charts — sorted by total value desc
  const companies = [...new Set(detail.map((d) => d.companyName))]
    .filter(Boolean)
    .sort((a, b) => {
      const aVal = detail.filter((d) => d.companyName === a).reduce((s, r) => s + r.valueOfInterest, 0);
      const bVal = detail.filter((d) => d.companyName === b).reduce((s, r) => s + r.valueOfInterest, 0);
      return bVal - aVal;
    });

  // ── Blur gate for free users ──────────────────────────────────────────────
  const proGate = !isPro;

  if (loading) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-4 bg-white/10 rounded w-2/3 mb-3" />
              <div className="h-3 bg-white/5 rounded w-1/3" />
            </div>
          ))}
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-10">
      {/* ── Print styles ──────────────────────────────────────────────────── */}
      <style>{`
        @media print {
          @page { margin: 0.75in; size: letter; }
          body {
            background: white !important;
            color: #111 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          /* Hide everything not meant for print */
          nav, header, footer,
          .print-hide { display: none !important; }

          /* Force all divs with inline background-color to print */
          div[style] { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          div[style*="background-color: rgba(251,191,36"] { background-color: #d97706 !important; }
          div[style*="background-color: rgba(139,92,246"] { background-color: #7c3aed !important; }
          div[style*="background-color: rgba(34,197,94"]  { background-color: #16a34a !important; }
          div[style*="background-color: rgba(59,130,246"] { background-color: #2563eb !important; }
          div[style*="background-color: rgba(239,68,68"]  { background-color: #dc2626 !important; }
          div[style*="background-color: rgba(249,115,22"] { background-color: #ea580c !important; }
          div[style*="background-color: rgba(20,184,166"] { background-color: #0f766e !important; }
          div[style*="background-color: rgba(236,72,153"] { background-color: #db2777 !important; }
          div[style*="background-color: rgba(255,255,255,0.04"] { display: none !important; }
          div[style*="background-color: rgba(255,255,255,0.07"] { display: none !important; }
          main { background: white !important; max-width: 100% !important; padding: 0 !important; }
          .card { background: white !important; border: 1px solid #e5e7eb !important; border-radius: 8px !important; box-shadow: none !important; break-inside: avoid; }
          .text-white { color: #111 !important; }
          .text-white\\/80, .text-white\\/70, .text-white\\/60, .text-white\\/50 { color: #374151 !important; }
          .text-white\\/40, .text-white\\/30, .text-white\\/25, .text-white\\/20 { color: #6b7280 !important; }
          .text-amber-400 { color: #b45309 !important; }
          .text-green-400 { color: #16a34a !important; }
          .text-teal-400 { color: #0f766e !important; }
          .text-red-400 { color: #dc2626 !important; }
          .text-orange-400 { color: #ea580c !important; }
          .text-violet-400 { color: #7c3aed !important; }
          .bg-red-500\\/10 { background: #fef2f2 !important; border-color: #fca5a5 !important; }
          .bg-amber-500\\/10 { background: #fffbeb !important; border-color: #fcd34d !important; }
          .bg-teal-500\\/10 { background: #f0fdfa !important; border-color: #5eead4 !important; }
          .bg-blue-500\\/10 { background: #eff6ff !important; border-color: #93c5fd !important; }
          .bg-orange-500\\/10 { background: #fff7ed !important; border-color: #fdba74 !important; }
          svg text { fill: #374151 !important; }

          /* Force chart bars to full opacity on white background */
          [style*="rgba(251,191,36"] { background-color: #d97706 !important; opacity: 1 !important; }
          [style*="rgba(139,92,246"] { background-color: #7c3aed !important; opacity: 1 !important; }
          [style*="rgba(34,197,94"]  { background-color: #16a34a !important; opacity: 1 !important; }
          [style*="rgba(59,130,246"] { background-color: #2563eb !important; opacity: 1 !important; }
          [style*="rgba(239,68,68"]  { background-color: #dc2626 !important; opacity: 1 !important; }
          [style*="rgba(249,115,22"] { background-color: #ea580c !important; opacity: 1 !important; }
          [style*="rgba(20,184,166"] { background-color: #0f766e !important; opacity: 1 !important; }
          [style*="rgba(236,72,153"] { background-color: #db2777 !important; opacity: 1 !important; }
          [style*="rgba(255,255,255,0.04"] { display: none !important; }

          /* Hide chart interactive controls */
          select { display: none !important; }

          /* Hide Export to CSV buttons inside chart cards */
          .card button { display: none !important; }

          /* Hide footnote rows inside chart cards */
          .card .flex.items-center.justify-between.flex-wrap { display: none !important; }
          .card .mt-3.flex.items-center { display: none !important; }

          /* Hide "show company breakdown" toggle and footnote in PreclinicalSplit */
          .card .border-t.border-white\\/5 button { display: none !important; }
          .card .border-t.border-white\\/5 p { display: none !important; }

          /* Hide the header row controls (dropdown + toggle) inside chart cards */
          .card .flex.items-center.justify-between.mb-4 > div:last-child { display: none !important; }
          .card .flex.items-center.gap-2:last-child { display: none !important; }
        }
      `}</style>

      {/* ── Print-only report header ───────────────────────────────────────── */}
      <div className="hidden print:block mb-6 pb-4 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">ProviderPayWatch · Ownership &amp; Investment Report</p>
            <h1 className="text-2xl font-bold text-gray-900">{name}</h1>
            <p className="text-sm text-gray-500 mt-1">
              NPI: {npi}
              {specialty ? ` · ${specialty}` : ""}
              {providerState ? ` · ${providerState}` : ""}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
            <p className="text-xs text-gray-400 mt-0.5">providerpaywatch.com</p>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Source: CMS Open Payments Ownership &amp; Investment Interest (2016–2025) · Reported by applicable manufacturers and GPOs · This report supports compliance research and risk review — consult qualified legal or compliance counsel for final determinations.
        </p>
      </div>

      {/* ── Back link ─────────────────────────────────────────────────────── */}
      <Link
        to={`/physician/${npi}?name=${encodeURIComponent(name)}&state=${providerState}`}
        className="print:hidden text-white/40 hover:text-white/70 text-sm transition-colors"
      >
        ← Back to provider profile
      </Link>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="mt-4 mb-6">
        <div className="print:hidden flex items-center gap-2 mb-1">
          <span className="text-amber-400/60 text-xs uppercase tracking-widest">Ownership &amp; Investment</span>
        </div>
        <h1 className="print:hidden font-display text-4xl text-white leading-tight">{name}</h1>
        <div className="print:hidden flex items-center gap-3 mt-1 flex-wrap">
          {npi && npi !== "unknown" ? (
            <p className="text-white/30 text-xs font-mono">NPI: {npi}</p>
          ) : null}
          {specialty && (
            <p className="text-white/30 text-xs">{specialty}</p>
          )}
          {providerState && (
            <p className="text-white/30 text-xs">{providerState}</p>
          )}
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className={`print:hidden flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border transition-all ${
              copied
                ? "text-green-400 border-green-500/30 bg-green-500/10"
                : "text-white/60 border-white/20 hover:text-white hover:border-white/40 bg-white/5"
            }`}
          >
            {copied
              ? <><span>✓</span><span>Link copied!</span></>
              : <><span>⎘</span><span>Share</span></>}
          </button>
          <div className="print:hidden">
            <WatchProviderButton
              npi={npi}
              providerName={name}
              initialWatched={isWatched}
              onWatchChange={(npi, watched) => setIsWatched(watched)}
            />
          </div>
          <div className="print:hidden">
            <CompareButton provider={{
              npi,
              firstName: name.split(" ")[0] || "",
              lastName: name.split(" ").slice(1).join(" ") || "",
              state: providerState,
              totalPayments: totalCurrentValue,
            }} />
          </div>
          {isPro ? (
            <button
              onClick={() => window.print()}
              className="print:hidden flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-white/20 text-white/60 hover:text-white hover:border-white/40 bg-white/5 transition-all"
            >
              <span>⎙</span><span>Print / Save PDF</span>
            </button>
          ) : (
            <button
              onClick={() => { setUpgradeMessage("Upgrade to Pro to print and save ownership profiles as PDF."); setShowUpgradeModal(true); }}
              className="print:hidden flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-white/20 text-white/40 bg-white/5 transition-all"
            >
              <span>🔒</span><span>Print / Save PDF</span>
            </button>
          )}
        </div>

        {/* ── No ownership data ────────────────────────────────────────── */}
        {ownership.length === 0 && (
          <div className="card text-center py-12 mt-6">
            <p className="text-white/50 text-sm">No ownership or investment interest records found for this provider.</p>
            <p className="text-white/25 text-xs mt-2">CMS Open Payments Ownership data covers 2016–2025.</p>
          </div>
        )}

        {/* ── Exclusion Banners ─────────────────────────────────────────── */}
        {!exclusionLoading && (exclusions.oig.length > 0 || exclusions.sam.length > 0) && (
          <div className="mt-4 space-y-2">
            {exclusions.oig.map((e, i) => (
              <div key={i} className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                  <div className="flex items-start gap-2">
                    <span className="text-red-400 text-sm mt-0.5">⚠</span>
                    <div>
                      <p className="text-red-400 font-medium text-sm">OIG Excluded</p>
                      {e.matchType === "name_state" && (
                        <p className="text-yellow-400/80 text-xs mt-0.5">Possible name and state match — verify manually</p>
                      )}
                    </div>
                  </div>
                  <div className="sm:text-right ml-6 sm:ml-0">
                    <p className="text-red-300/70 text-xs">{e.exclusionDate}</p>
                    <p className="text-red-300/50 text-xs mt-0.5">{e.exclusionType}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-red-500/20">
                  {e.reinstatedDate && <p className="text-green-400 text-xs">Reinstated: {e.reinstatedDate}</p>}
                  <a href="https://exclusions.oig.hhs.gov" target="_blank" rel="noopener noreferrer"
                    className="text-red-400/50 hover:text-red-400 text-xs transition-colors ml-auto">
                    Verify on OIG website →
                  </a>
                </div>
              </div>
            ))}
            {exclusions.sam.map((e, i) => (
              <div key={i} className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                  <div className="flex items-start gap-2">
                    <span className="text-orange-400 text-sm mt-0.5">⚠</span>
                    <div>
                      <p className="text-orange-400 font-medium text-sm">SAM.gov Excluded</p>
                      {e.matchType === "name_state" && (
                        <p className="text-yellow-400/80 text-xs mt-0.5">Possible name and state match — verify manually</p>
                      )}
                    </div>
                  </div>
                  <div className="sm:text-right ml-6 sm:ml-0">
                    <p className="text-orange-300/70 text-xs">{e.exclusionDate}</p>
                    <p className="text-orange-300/50 text-xs mt-0.5">{e.exclusionType}</p>
                    {e.excludingAgency && <p className="text-orange-300/40 text-xs mt-0.5">{e.excludingAgency}</p>}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-orange-500/20">
                  {e.terminationDate && e.terminationDate !== "Indefinite" && (
                    <p className="text-orange-300/50 text-xs">Terminates: {e.terminationDate}</p>
                  )}
                  <a href="https://sam.gov/search/?index=ei&page=1&pageSize=25&sort=-score&sfm%5Bstatus%5D%5B0%5D=Active"
                    target="_blank" rel="noopener noreferrer"
                    className="text-orange-400/50 hover:text-orange-400 text-xs transition-colors ml-auto">
                    Verify on SAM.gov →
                  </a>
                </div>
              </div>
            ))}
            <p className="text-white/20 text-xs">
              Sources: HHS OIG LEIE (updated monthly) · SAM.gov (updated daily) · Some records may contain data from Dun &amp; Bradstreet.
            </p>
          </div>
        )}

        {/* ── Conflict Banners ──────────────────────────────────────────── */}
        {(hasTripleInterest || hasDualInterest || dualResearchInterest.length > 0 || podFlags.length > 0) && (
          <div className="mt-4 space-y-3">
            <OwnersConflictBanners
              hasTripleInterest={hasTripleInterest}
              tripleInterestCompanyIds={tripleInterestCompanyIds}
              hasDualInterest={hasDualInterest}
              hasDualInterestSamePeriod={hasDualInterestSamePeriod}
              dualInterest={dualInterest}
              dualResearchInterest={dualResearchInterest}
              researchGeneralDualInterest={researchGeneralDualInterest}
              hasResearchGeneralDual={hasResearchGeneralDual}
              directResearch={directResearch}
              directResearchCompanyIds={directResearchCompanyIds}
              podFlags={podFlags}
              isPro={isPro}
              setUpgradeMessage={setUpgradeMessage}
              setShowUpgradeModal={setShowUpgradeModal}
            />
          </div>
        )}

        {/* ── Page tab bar ──────────────────────────────────────────────── */}
        <div className="print:hidden mt-4">
          <ProviderPageTabs
            npi={npi}
            name={name}
            state={providerState}
            specialty={specialty}
            totalPayments={totalPayments}
            ownershipCount={ownership.length}
            activeOwnershipCount={ownership.filter((o) => o.hasActiveOwnership).length}
            researchCount={research.length}
            researchPayments={totalResearchPayments}
            researchYears={researchYearCount}
            activePage="ownership"
          />
        </div>

        {/* ── Stats Row ─────────────────────────────────────────────────── */}
        {ownership.length > 0 && (
          <div className="flex gap-3 mt-4 flex-wrap">
            <div className={`card py-3 px-4 text-center ${proGate ? "relative overflow-hidden" : ""}`}>
              {proGate && <div className="absolute inset-0 backdrop-blur-sm bg-navy-900/40 z-10 rounded-xl" />}
              <div className="text-amber-400 font-display text-2xl">
                {proGate ? formatCurrency(99999) : formatCurrency(totalCurrentValue)}
              </div>
              <div className="text-white/30 text-xs mt-0.5">active interest value</div>
            </div>
            <div className="card py-3 px-4 text-center">
              <div className="text-amber-400 font-display text-2xl">{ownership.length}</div>
              <div className="text-white/30 text-xs mt-0.5">
                {ownership.length === 1 ? "company" : "companies"}
              </div>
            </div>
            <div className="card py-3 px-4 text-center">
              <div className="text-green-400 font-display text-2xl">{activeOwnership.length}</div>
              <div className="text-white/30 text-xs mt-0.5">active (2025)</div>
            </div>
          </div>
        )}
      </div>

      {/* ── Charts ────────────────────────────────────────────────────────── */}
      {detail.length > 0 && (
        <div className={`relative ${proGate ? "overflow-hidden rounded-2xl" : ""}`}>
          {proGate && (
            <div className="absolute inset-0 backdrop-blur-md bg-navy-900/60 z-10 rounded-2xl flex flex-col items-center justify-center gap-3 p-6">
              <p className="text-white/70 font-medium text-sm text-center">
                🔒 Ownership history charts are a Pro feature
              </p>
              <p className="text-white/40 text-xs text-center max-w-xs">
                Upgrade to Pro to see year-by-year ownership value trends and company breakdowns.
              </p>
              <button
                onClick={() => {
                  setUpgradeMessage("Upgrade to Pro to access full ownership history charts, value trends, and company breakdowns.");
                  setShowUpgradeModal(true);
                }}
                className="btn-primary text-xs px-5 py-2 mt-1"
              >
                Upgrade to Pro
              </button>
            </div>
          )}

          {/* Stacked bar chart — by company (absolute + normalized) */}
          <div className="print-chart-wrapper card mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white/60 text-xs uppercase tracking-widest">
                Value by company
              </h2>
              <div className="flex items-center gap-2">
                <CompanyDropdown
                  companies={companies}
                  selectedCompanies={selectedCompanies}
                  onToggle={toggleCompany}
                />
                {/* View toggle */}
                <div className="flex items-center bg-navy-700 border border-white/10 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setStackedView("absolute")}
                    className={`text-xs px-2.5 py-1.5 transition-all ${
                      stackedView === "absolute"
                        ? "bg-amber-500/20 text-amber-400"
                        : "text-white/40 hover:text-white/60"
                    }`}
                  >
                    $
                  </button>
                  <button
                    onClick={() => setStackedView("normalized")}
                    className={`text-xs px-2.5 py-1.5 transition-all ${
                      stackedView === "normalized"
                        ? "bg-amber-500/20 text-amber-400"
                        : "text-white/40 hover:text-white/60"
                    }`}
                  >
                    %
                  </button>
                </div>
              </div>
            </div>
            {stackedView === "absolute" ? (
              <OwnershipStackedBarChart
                data={detail}
                companies={companies}
                selectedCompanies={selectedCompanies}
                isPro={isPro}
              />
            ) : (
              <NormalizedStackedBarChart
                data={detail}
                companies={companies}
                selectedCompanies={selectedCompanies}
              />
            )}
            {/* Legend */}
            {companies.length > 1 && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
                {companies.slice(0, 8).map((c, i) => {
                  const COLORS = [
                    "bg-amber-400","bg-violet-500","bg-green-500","bg-blue-500",
                    "bg-red-500","bg-orange-500","bg-teal-500","bg-pink-500",
                  ];
                  return (
                    <div key={c} className="flex items-center gap-1.5">
                      <div className={`w-2 h-2 rounded-sm ${COLORS[i % COLORS.length]} opacity-80`} />
                      <span className="text-white/40 text-xs truncate max-w-32">
                        {c.length > 25 ? c.slice(0, 25) + "..." : c}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Export button */}
            <div className="mt-4 flex justify-end border-t border-white/5 pt-3">
              {isPro ? (
                <button
                  onClick={() => exportOwnershipDetailCsv(
                    detail.filter(d =>
                      selectedCompanies.has("ALL") || selectedCompanies.has(d.companyName)
                    ),
                    name,
                    npi,
                    EXPORT_LIMIT
                  )}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all"
                >
                  ⬇ Export to CSV
                </button>
              ) : (
                <p className="text-white/20 text-xs">Pro subscribers can export results to CSV.</p>
              )}
            </div>
          </div>

          {/* Interest type donut — screen only per scope decision */}
          <div className="print:hidden">
            <InterestTypeDonut ownership={ownership} />
          </div>

          {/* Physician vs family member split — prints when family/joint involvement exists */}
          <HolderSplit ownership={ownership} />

          {/* Active vs historical interests — screen only */}
          <div className="print:hidden">
            <ActiveVsHistorical ownership={ownership} />
          </div>

          {/* Chart A: General payments vs ownership value — screen only */}
          <div className="print:hidden">
            {hasDualInterest && generalPayments.length > 0 && (
              <GeneralVsOwnershipChart
                conflicts={dualInterest}
                generalPayments={generalPayments}
                ownershipDetail={detail}
                providerName={name}
                npi={npi}
              />
            )}
          </div>

          {/* Chart B: Ownership value vs research funding — screen only */}
          <div className="print:hidden">
            {dualResearchInterest.length > 0 && (
              <OwnershipVsResearchChart
                conflicts={dualResearchInterest}
                ownershipDetail={detail}
                researchDetail={researchDetail}
                directResearch={directResearch}
                providerName={name}
                npi={npi}
              />
            )}
          </div>
        </div>
      )}

      {/* ── Ownership Records ─────────────────────────────────────────────── */}
      {ownership.length > 0 && (
        <>
          {/* Print-only top-5 ownership list */}
          <div className="hidden print:block mt-6 mb-4">
            <h2 className="text-gray-500 text-xs uppercase tracking-widest mb-3">Ownership &amp; Investment Interests (Top 5)</h2>
            <div className="space-y-2">
              {ownership.slice(0, 5).map((o, i) => {
                const isTriple = tripleInterestCompanyIds.has(o.companyId);
                const isDual = !isTriple && (
                  dualInterest.some((d) => d.companyId === o.companyId) ||
                  dualResearchInterest.some((r) => r.companyId === o.companyId)
                );
                return (
                  <div key={i} className={`border rounded-lg px-4 py-3 ${o.hasActiveOwnership ? "border-amber-200 bg-amber-50" : "border-gray-200"}`}>
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-gray-900 text-sm font-medium">{o.companyName}</p>
                          {o.hasActiveOwnership && <span className="text-xs text-green-700 border border-green-200 bg-green-50 px-1.5 py-0.5 rounded-full">Active 2025</span>}
                          {isTriple && <span className="text-xs text-red-600 border border-red-200 bg-red-50 px-1.5 py-0.5 rounded-full">⚠ Triple Interest</span>}
                          {isDual && <span className="text-xs text-orange-600 border border-orange-200 bg-orange-50 px-1.5 py-0.5 rounded-full">⚑ Dual Interest</span>}
                        </div>
                        <p className="text-gray-500 text-xs mt-0.5">
                          {o.firstYear === o.latestYear ? `Reported ${o.firstYear}` : `${o.firstYear}–${o.latestYear} · ${o.yearCount} year${o.yearCount !== 1 ? "s" : ""}`}
                        </p>
                        <p className="text-gray-500 text-xs mt-0.5">
                          {o.interestHolder === "Physician and Immediate Family Member" ? "Physician and immediate family member"
                            : o.interestHolder === "Immediate Family Member" ? "Immediate family member"
                            : "Physician"}
                        </p>
                        {o.termsOfInterest && (
                          <p className="text-gray-400 text-xs mt-1 italic">{o.termsOfInterest}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        {o.valueOfInterest > 0 && (
                          <>
                            <p className="text-amber-700 text-sm font-mono font-medium">{formatCurrency(o.valueOfInterest)}</p>
                            <p className="text-gray-400 text-xs">interest value</p>
                          </>
                        )}
                        {o.totalAmountInvested > 0 && (
                          <>
                            <p className="text-gray-600 text-xs font-mono mt-1">{formatCurrency(o.totalAmountInvested)}</p>
                            <p className="text-gray-400 text-xs">amount invested</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {ownership.length > 5 && (
              <p className="text-gray-400 text-xs mt-2">Showing 5 of {ownership.length} companies · export full list from ProviderPayWatch</p>
            )}
          </div>

          <div className="print:hidden flex items-center justify-between mb-3">
            <h2 className="text-white/40 text-xs uppercase tracking-widest">
              Ownership interests
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-white/25 text-xs">2016–2025</span>
              {isPro && (
                <button
                  onClick={() => exportCsv(ownership, name, npi, tripleInterestCompanyIds, dualInterest, dualResearchInterest, EXPORT_LIMIT)}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all"
                >
                  ⬇ Export all to CSV
                </button>
              )}
            </div>
          </div>

          <div className={`print:hidden relative space-y-3 ${proGate ? "overflow-hidden rounded-2xl" : ""}`}>
            {proGate && (
              <div className="absolute inset-0 backdrop-blur-md bg-navy-900/60 z-10 rounded-2xl flex flex-col items-center justify-center gap-3 p-6">
                <p className="text-white/70 font-medium text-sm text-center">
                  🔒 Full ownership records are a Pro feature
                </p>
                <p className="text-white/40 text-xs text-center max-w-xs">
                  Upgrade to Pro to see company names, interest values, terms, and year-by-year history.
                </p>
                <button
                  onClick={() => {
                    setUpgradeMessage("Upgrade to Pro to access full ownership and investment interest records.");
                    setShowUpgradeModal(true);
                  }}
                  className="btn-primary text-xs px-5 py-2 mt-1"
                >
                  Upgrade to Pro
                </button>
              </div>
            )}

            {/* Show blurred preview for free users or full records for Pro */}
            {(proGate ? ownership.slice(0, 3) : ownership).map((o, i) => (
              <div
                key={i}
                className={`rounded-xl border px-4 py-4 ${
                  o.hasActiveOwnership
                    ? "bg-amber-500/5 border-amber-500/20"
                    : "bg-white/[0.03] border-white/[0.08]"
                }`}
              >
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white/80 text-sm font-medium truncate">
                        {proGate ? "Company Name Hidden" : o.companyName}
                      </p>
                      {o.hasActiveOwnership && (
                      <span className="badge bg-green-500/20 border border-green-500/30 text-green-400 text-xs">
                        Active 2025
                      </span>
                    )}
                    {tripleInterestCompanyIds.has(o.companyId) ? (
                      <span className="badge bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
                        ⚠ Triple Interest
                      </span>
                    ) : (dualInterest.some((d) => d.companyId === o.companyId) ||
                         dualResearchInterest.some((r) => r.companyId === o.companyId)) && (
                      <span className="badge bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs">
                        ⚑ Dual Interest
                      </span>
                    )}
                    </div>
                    <p className="text-white/30 text-xs mt-0.5">
                      {o.firstYear === o.latestYear
                        ? `Reported ${o.firstYear}`
                        : `${o.firstYear}–${o.latestYear} · ${o.yearCount} year${o.yearCount !== 1 ? "s" : ""}`}
                    </p>
                    <p className="text-white/40 text-xs mt-1">
                      {o.interestHolder === "Physician and Immediate Family Member"
                        ? "Physician and immediate family member"
                        : o.interestHolder === "Immediate Family Member"
                        ? "Immediate family member"
                        : "Physician"}
                    </p>
                    {o.termsOfInterest && !proGate && (
                      <p className="text-white/25 text-xs mt-1.5 italic leading-relaxed">
                        {o.termsOfInterest}
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    {o.valueOfInterest > 0 && (
                      <>
                        <p className={`text-sm font-mono font-medium ${proGate ? "blur-sm select-none" : "text-amber-400"}`}>
                          {formatCurrency(o.valueOfInterest)}
                        </p>
                        <p className="text-white/30 text-xs">interest value</p>
                      </>
                    )}
                    {o.totalAmountInvested > 0 && (
                      <>
                        <p className={`text-xs font-mono mt-1 ${proGate ? "blur-sm select-none" : "text-white/50"}`}>
                          {formatCurrency(o.totalAmountInvested)}
                        </p>
                        <p className="text-white/25 text-xs">amount invested</p>
                      </>
                    )}
                  </div>
                </div>
                {isPro && !proGate && (
                  <div className="mt-3 flex justify-end gap-2">
                    {isEnterprise ? (
                      <button
                        onClick={() => setScreenPanelCompanyId(screenPanelCompanyId === o.companyId ? null : o.companyId)}
                        className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-all"
                      >
                        🛡 Screen
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setUpgradeMessage("Screening a company for conflicts against this provider is an Enterprise feature.");
                          setShowUpgradeModal(true);
                        }}
                        className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-red-500/30 bg-red-500/5 text-red-400/70 hover:bg-red-500/10 hover:text-red-400 transition-all"
                      >
                        🔒 Screen <span className="text-[9px] px-1 py-0.5 rounded-full bg-red-500/20 border border-red-500/30 text-red-300 uppercase tracking-wide">Enterprise</span>
                      </button>
                    )}
                    <button
                      onClick={(e) => handleCompanyWatchToggle(e, o.companyName, o.companyId)}
                      disabled={companyWatchLoading.has(o.companyName)}
                      className={`text-xs px-3 py-1 rounded-lg border transition-all ${
                        watchedCompanyNames.get(o.companyName)
                          ? "bg-gold-500/20 border-gold-500/40 text-gold-300"
                          : "border-white/20 text-white/40 hover:border-gold-500/40 hover:text-gold-300"
                      }`}
                    >
                      {companyWatchLoading.has(o.companyName)
                        ? "..."
                        : watchedCompanyNames.get(o.companyName)
                        ? "🔔 Watching"
                        : "🔕 Watch"}
                    </button>
                  </div>
                )}
                {screenPanelCompanyId === o.companyId && (
                  <SingleProviderScreenPanel
                    companyId={o.companyId}
                    companyName={o.companyName}
                    npi={npi}
                    providerName={name}
                    userId={user?.id}
                    fetchFn={apiFetch}
                    onCancel={() => setScreenPanelCompanyId(null)}
                  />
                )}
              </div>
            ))}

            {/* Show count of hidden records for free users */}
            {proGate && ownership.length > 3 && (
              <div className="text-center text-white/30 text-xs pt-2 pb-4">
                +{ownership.length - 3} more record{ownership.length - 3 !== 1 ? "s" : ""} hidden
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Export — screen only ─────────────────────────────────────────── */}
      {ownership.length > 0 && (
        <div className="print:hidden mt-3 flex items-center justify-between flex-wrap gap-2">
          <span className="text-white/25 text-xs">{ownership.length} {ownership.length === 1 ? "company" : "companies"}</span>
          {isPro ? (
            <button
              onClick={() => exportCsv(ownership, name, npi, tripleInterestCompanyIds, dualInterest, dualResearchInterest)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all"
            >
              ⬇ Export all to CSV
            </button>
          ) : (
            <p className="text-white/20 text-xs">Pro subscribers can export results to CSV.</p>
          )}
        </div>
      )}

      <p className="print:hidden text-white/20 text-xs text-center mt-8">
        Source: CMS Open Payments Ownership &amp; Investment Interest · Reported by applicable manufacturers and GPOs as required by the Physician Payments Sunshine Act
      </p>
      <p className="hidden print:block text-gray-400 text-xs text-center mt-8">
        Source: CMS Open Payments Ownership &amp; Investment Interest (2016–2025) · providerpaywatch.com · Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
      </p>
    </main>
  );
}

function exportOwnershipDetailCsv(detail, name, npi, exportLimit = 500) {
  const rows = [
    ["Provider Name", "NPI", "Company", "Year", "Interest Value ($)", "% of Year Total", "Amount Invested ($)", "Interest Holder", "Terms"],
  ];
  const exportData = exportLimit === Infinity ? detail : detail.slice(0, exportLimit);

  // Pre-compute year totals for % calculation
  const yearTotals = {};
  for (const d of exportData) {
    yearTotals[d.programYear] = (yearTotals[d.programYear] || 0) + d.valueOfInterest;
  }

  for (const d of exportData) {
    const yearTotal = yearTotals[d.programYear] || 0;
    const pct = yearTotal > 0 ? ((d.valueOfInterest / yearTotal) * 100).toFixed(1) + "%" : "0.0%";
    rows.push([
      name,
      npi,
      d.companyName,
      d.programYear,
      d.valueOfInterest.toFixed(2),
      pct,
      d.totalAmountInvested.toFixed(2),
      d.interestHolder,
      d.termsOfInterest,
    ]);
  }  
  const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${name.replace(/\s+/g, "_")}_ownership_by_company.csv`;
  a.click();
}


function exportCsv(ownership, name, npi, tripleInterestCompanyIds, dualInterest, dualResearchInterest, exportLimit = 500) {    
  const rows = [
    ["Provider Name", "NPI", "Company", "Interest Value ($)", "Amount Invested ($)", "Interest Holder", "Terms", "First Year", "Latest Year", "Active 2025", "Interest Flag"],
  ];
  const exportData = exportLimit === Infinity ? ownership : ownership.slice(0, exportLimit);
  for (const o of exportData) {
    const isTriple = tripleInterestCompanyIds.has(o.companyId);
                  const isDual = !isTriple && (
                    dualInterest.some((d) => d.companyId === o.companyId) ||
                    dualResearchInterest.some((r) => r.companyId === o.companyId) ||
                    directResearchCompanyIds.has(o.companyId)
                  );
    const flag = isTriple ? "Triple Interest" : isDual ? "Dual Interest" : "";
    rows.push([
      name,
      npi,
      o.companyName,
      o.valueOfInterest.toFixed(2),
      o.totalAmountInvested.toFixed(2),
      o.interestHolder,
      o.termsOfInterest,
      o.firstYear,
      o.latestYear,
      o.hasActiveOwnership ? "Yes" : "No",
      flag,
    ]);
  }
  const csv = rows.map((r) => r.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${name.replace(/\s+/g, "_")}_ownership.csv`;
  a.click();
}